import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useElementScrollRestoration } from '@tanstack/react-router';
import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';
import { useWindowVirtualizer } from '@tanstack/react-virtual';

interface InfiniteScrollListProps<T> {
  infiniteQuery: UseInfiniteQueryResult<
    InfiniteData<{
      hasMore: boolean;
      list: T[];
    }>
  >;
  renderItem: (item: T) => React.ReactNode;
  estimateSizeFn?: (item: T) => number;
  estimateSizeNumber?: number;
  paddingEnd?: number;
  gap?: number;
  px?: number;
  shouldPauseFetching?: (
    data?: InfiniteData<{
      hasMore: boolean;
      list: T[];
    }>,
  ) => boolean;
  renderSeeMoreButton?: () => React.ReactNode;
  select?: (flattenedData: T[]) => T[];
}

const InfiniteScrollList = <T,>({
  infiniteQuery,
  renderItem,
  estimateSizeFn,
  estimateSizeNumber,
  paddingEnd,
  gap,
  px = 6,
  shouldPauseFetching,
  renderSeeMoreButton,
  select,
}: InfiniteScrollListProps<T>) => {
  const { data, fetchNextPage, status, error, hasNextPage, isFetchingNextPage } = infiniteQuery;

  const flattenedData = useMemo(() => {
    const originalFlattenedData = data?.pages.flatMap((page) => page.list) ?? [];
    return select ? select(originalFlattenedData) : originalFlattenedData;
  }, [data, select]);

  const listRef = useRef<HTMLDivElement>(null);

  const scrollEntry = useElementScrollRestoration({ getElement: () => window });

  const rowVirtualizer = useWindowVirtualizer({
    count: hasNextPage ? flattenedData.length + 1 : flattenedData.length,
    estimateSize: (index) => estimateSizeFn?.(flattenedData[index]) ?? estimateSizeNumber ?? 40,
    overscan: 5,
    gap: gap ?? 16,
    paddingEnd: paddingEnd ?? 160,
    scrollMargin: listRef.current?.offsetTop ?? 0,
    initialOffset: scrollEntry?.scrollY,
  });

  useEffect(() => {
    const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();

    if (!lastItem) {
      return;
    }

    if (
      lastItem.index >= flattenedData.length - 1 &&
      hasNextPage &&
      !isFetchingNextPage &&
      !shouldPauseFetching?.(data)
    ) {
      fetchNextPage();
    }
  }, [
    hasNextPage,
    fetchNextPage,
    flattenedData.length,
    isFetchingNextPage,
    rowVirtualizer.getVirtualItems(),
    data,
    shouldPauseFetching,
  ]);

  const renderLoaderRow = useCallback(() => {
    if (hasNextPage) {
      if (shouldPauseFetching?.(data)) {
        return renderSeeMoreButton?.() ?? null;
      }
      return <div>Loading more...</div>;
    }
    return null;
  }, [hasNextPage, shouldPauseFetching, data, renderSeeMoreButton]);

  return (
    <>
      {status === 'pending' ? (
        <p>Loading...</p>
      ) : status === 'error' ? (
        <span>Error: {error.message}</span>
      ) : (
        <div className={`w-full px-${px}`} ref={listRef}>
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const isLoaderRow = virtualRow.index > flattenedData.length - 1;
              const item = flattenedData[virtualRow.index];

              return (
                <div
                  key={virtualRow.key}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start - rowVirtualizer.options.scrollMargin}px)`,
                  }}
                >
                  {isLoaderRow ? renderLoaderRow() : renderItem(item)}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

export default InfiniteScrollList;
